import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonProperties } from '../../common/common.interfaces';
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../common/common.entities';
import { useLoading } from '../../loading/LoadingContext';
import { useUserContext } from '../../user/UserContext';
import { DateTime } from 'luxon';
import CustomError, { CustomErrorProps } from '../../common/components/CustomError';
import { apiFetch } from '../../utility/fetch';
import { API_DEV_URL, API_PROD_URL } from '../..';
import RefreshSvg from '../../common/components/svg/RefreshSvg';
import { Badge, Button, Form } from 'react-bootstrap';
import XSvg from '../../common/components/svg/X-Svg';
import { OrderItem, SalesforceQueryResponse } from '../orders.interfaces';
import { getOrderItemsQueryByLastNWeeksWithLimitOffset } from '../orders.queries';
import { notify } from '../../notify/notify';
import OrderEdit from './OrderEdit';
import OrderRoutingDebugModalComponent from './OrderRouting';
import EyeSvg from '../../common/components/svg/EyeSvg';

interface OrdersTableProps extends CommonProperties {
  handleShowContent: any
}

export default function OrdersTable(props: OrdersTableProps) {
  //UNSELECTS ANY NUMBER CONTROLS WHEN USER USES MOUSE WHEEL SO THAT IT DOES NOT CHANGE THE VALUE OF IT
  document.addEventListener("wheel", function(event){
      if(document.activeElement['type'] === "number"){
          document.activeElement['blur']();
      }
  });

  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()

  //#region STATE
  const [orderItems, setOrderItems]: [OrderItem[], any] = useState([])
  const [filteredRows, setFilteredRows]: [OrderItem[], any] = useState([])

  const [uniqueShippingStatuses, setUniqueShippingStatuses]: [string[], any] = useState([])
  const [uniqueOrderStatuses, setUniqueOrderStatuses]: [string[], any] = useState([])
  const [uniqueFulfillmentSoftwares, setUniqueFulfillmentSoftwares]: [string[], any] = useState([])
  const [uniqueAccounts, setUniqueAccounts]: [string[], any] = useState([])

  const [selectedAccount, setSelectedAccount]: [string, any] = useState('SELECT AN ACCOUNT')
  const [selectedFulfillmentSoftware, setSelectedFulfillmentSoftware]: [string, any] = useState('SELECT A FULFILLMENT SOFTWARE')
  const [selectedShippingStatus, setSelectedShippingStatus]: [string, any] = useState('SELECT A SHIPPING STATUS')
  const [selectedOrderStatus, setSelectedOrderStatus]: [string, any] = useState('SELECT AN ORDER STATUS')
  const [searchMpn, setSearchMpn]: [string, any] = useState('')
  const [searchOrderNumber, setSearchOrderNumber]: [string, any] = useState('')

  const [refresh, setRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100)

  const [weeks, setWeeks] = useState(1)
  const [limit, setLimit] = useState(2500)

  const [oldestOrderTime, setOldestOrderTime] = useState(undefined)

  const [copyBlob, setCopyBlob] = useState(undefined)
  const [copyBlobOrderNumber, setCopyBlobOrderNumber] = useState(undefined)

  const [routingDebugBlob, setRoutingDebugBlob] = useState(undefined)
  const [routingDebugBlobName, setRoutingDebugBlobName] = useState(undefined)

  //#region LOADING
  const load = useCallback(async() => {
    changeStep('Mission Control Orders')
    changeSubStep('Loading Mission Control Order Items')
    toggleLoading(true)

    const response = await apiFetch<SalesforceQueryResponse<OrderItem>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      body: JSON.stringify({
        soql: getOrderItemsQueryByLastNWeeksWithLimitOffset(weeks, limit) 
      })
    })

    const shippingStatuses: string[] = []
    const orderStatuses: string[] = []
    const accounts: string[] = []
    const fulfillmentSoftwares: string[] = []
    let index = 0
    for(const row of response.body.records){
      index += 1
      row.index = index
      if(row.Shipping_Detail__r?.Shipping_Status__c && !shippingStatuses.includes(row.Shipping_Detail__r?.Shipping_Status__c)){
        shippingStatuses.push(row.Shipping_Detail__r?.Shipping_Status__c)
      }
      if(row.Order?.Account?.Name && !accounts.includes(row.Order?.Account?.Name)){
        accounts.push(row.Order?.Account?.Name)
      }
      if(row.Shipping_Detail__r?.Fulfilled_by_Software__c && !fulfillmentSoftwares.includes(row.Shipping_Detail__r?.Fulfilled_by_Software__c)){
        fulfillmentSoftwares.push(row.Shipping_Detail__r?.Fulfilled_by_Software__c)
      }
      if(row.Order?.Status__c && !orderStatuses.includes(row.Order?.Status__c)){
        orderStatuses.push(row.Order?.Status__c)
      }
    }

    setUniqueShippingStatuses(shippingStatuses.sort((a, b) => a.localeCompare(b)))
    setUniqueAccounts(accounts.sort((a, b) => a.localeCompare(b)))
    setUniqueFulfillmentSoftwares(fulfillmentSoftwares.sort((a, b) => a.localeCompare(b)))
    setUniqueOrderStatuses(orderStatuses.sort((a,b) => a.localeCompare(b)))

    setOrderItems(response.body.records)
    setOldestOrderTime(DateTime.fromISO(response.body.records[response.body.records.length-1].Order.Order_Time__c).toFormat('MM/dd/yyyy t'))

    toggleLoading(false)
  }, [limit, weeks])

  useEffect(() => {
    load()
  }, [refresh])

  //#region CALLBACKS
  const handleShippingStatusChanged = useCallback((e) => {
    if(e.currentTarget.value === 'SELECT A SHIPPING STATUS') return
    setFilteredRows(orderItems.filter(oi => oi.Shipping_Detail__r?.Shipping_Status__c === e.currentTarget.value))
    setSelectedShippingStatus(e.currentTarget.value)
  }, [orderItems])

  const handleOrderStatusChanged = useCallback((e) => {
    if(e.currentTarget.value === 'SELECT AN ORDER STATUS') return
    setFilteredRows(orderItems.filter(oi => oi.Order?.Status__c === e.currentTarget.value))
    setSelectedOrderStatus(e.currentTarget.value)
  }, [orderItems])

  const handleFulfillmentSoftwareChanged = useCallback((e) => {
    if(e.currentTarget.value === 'SELECT A FULFILLMENT SOFTWARE') return
    setFilteredRows(orderItems.filter(oi => oi.Shipping_Detail__r?.Fulfilled_by_Software__c === e.currentTarget.value))
    setSelectedFulfillmentSoftware(e.currentTarget.value)
  }, [orderItems])

  const handleAccountChanged = useCallback((e) => {
    if(e.currentTarget.value === 'SELECT AN ACCOUNT') return
    setFilteredRows(orderItems.filter(oi => oi.Order?.Account?.Name === e.currentTarget.value))
    setSelectedAccount(e.currentTarget.value)
  }, [orderItems])

  const handleSearchMpnChanged = useCallback((e) => {
    setFilteredRows(orderItems.filter(oi => oi.Product2?.MPN__c?.toLowerCase()?.includes(e.currentTarget.value.toLowerCase())))
  }, [orderItems])

  const handleSearchOrderNumberChanged = useCallback((e) => {
    setFilteredRows(orderItems.filter(oi => oi.Order?.Name.toLowerCase()?.includes(e.currentTarget.value.toLowerCase())))
  }, [orderItems])

  const handleClearFiltersClicked = useCallback(() => {
    setSelectedShippingStatus('SELECT A SHIPPING STATUS')
    setSelectedOrderStatus('SELECT AN ORDER STATUS')
    setSearchMpn('')
    setSearchOrderNumber('')
    setSelectedAccount('SELECT AN ACCOUNT')
    setSelectedFulfillmentSoftware('SELECT A FULFILLMENT SOFTWARE')
    setFilteredRows([])
  }, [])

  const handleSandboxClicked = useCallback(async (e) => {
    const {ordernumber, accountid, externalorderid} = e.currentTarget.dataset

    const chadIds = [
      '0011N00001jqJqRQAU',
      '0011N00001jqJqTQAU',
      '0011N00001jqJqSQAU',
      'a031N00000pSy98QAC',
      '0011N00001jqJqPQAU',
      '0013l00002QMZcyAAH',
      '0011N00001jqJq4QAE',
      '0011N00001jqJqCQAU',
      '0011N00001jqJqFQAU',
      '0011N00001jqJqIQAU',
      '0011N00001jqJqLQAU',
      '0011N00001jqJqOQAU',
      '0011N00001oW0kLQAS',
      '0011N00001sVmtvQAC',
      '0013l00002OXyWzAAL',
      '0011N00001jqJq6QAE',
      '0011N00001jqJqEQAU',
      '0016O00003XcljaQAB',
      '001RP00000QlqsGYAR'
    ]
    const isChannelAdvisor = chadIds.includes(accountid)

    changeStep('LOADING ORDER DATA')
    changeSubStep('Getting integration configuration record.')
    toggleLoading(true)
    let configs = {
      body: []
    }

    if(!isChannelAdvisor){
      configs = await apiFetch<any>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: API_PROD_URL,
        params: [
          {key: 'table', value: 'integrationConfiguration'},
          {key: 'filter', value: `accountId eq '${accountid}'`}
        ]
      })
    } else {
      configs.body.push({newOrdersChannelFolder: 'new/chad'})
    }

    //WAYFAIR CA is coming in under WAYFAIR US due to them sending CA through US integration
    if(accountid === '0011N00001oW92LQAS'){
      configs.body = [{newOrdersChannelFolder: 'new/wayfair/WAYU'}]
    }

    changeSubStep('Finding order blob path by order number.')
    let foundBlob = undefined
    const {newOrdersChannelFolder} = configs?.body[0]
    const path = `order-service/mapping/archive/${newOrdersChannelFolder}/${ordernumber}`
    const blobs = await apiFetch<any>({
      endpoint: 'listBlobs',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'prefix', value: path}
      ]
    })

    if(blobs.body.length === 0){
      changeSubStep('Finding order blob path by external order number.')

      //try checking external id
      const externalIdPath = `order-service/mapping/archive/${newOrdersChannelFolder}/${externalorderid}`
      const externalIdBlobs = await apiFetch<any>({
        endpoint: 'listBlobs',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: API_PROD_URL,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'prefix', value: externalIdPath}
        ]
      })
      if(externalIdBlobs.body.length > 0){
        foundBlob = externalIdBlobs?.body[0]
      }
    } else {
      foundBlob = blobs?.body[0]
    }
    changeSubStep('Getting production order blob data.')

    const blobResponse = await apiFetch({
      endpoint: 'blob',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: foundBlob.name}
      ]
    })
    if(blobResponse.ok){
      setCopyBlob(JSON.stringify(blobResponse.body))
      setCopyBlobOrderNumber(foundBlob.name)
    } else {
      notify('error', `Failed to load order data.`)
    }

    toggleLoading(false)
  }, [])

  const handleRoutingDebugClicked = useCallback(async (e) => {
    const {ordernumber, externalorderid, env} = e.currentTarget.dataset
    changeStep('LOADING ROUTING DATA')
    changeSubStep('Finding routing debug blobs.')
    toggleLoading(true)
    let foundBlob = undefined

    const path = `order-service/warehouse-routing/debug/${ordernumber}`
    const blobs = await apiFetch<any>({
      endpoint: 'listBlobs',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: env === 'PRODUCTION' ? API_PROD_URL : API_DEV_URL,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'prefix', value: path}
      ]
    })
    if(blobs.body.length === 0){
      //try by external order id
      const externalIdPath = `order-service/warehouse-routing/debug/${externalorderid}`
      const externalIdBlobs = await apiFetch<any>({
        endpoint: 'listBlobs',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: env === 'PRODUCTION' ? API_PROD_URL : API_DEV_URL,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'prefix', value: externalIdPath}
        ]
      })
      if(externalIdBlobs.body.length > 0){
        foundBlob = externalIdBlobs?.body[0]
      }
    } else {
      foundBlob = blobs.body[0]
    }

    if(!foundBlob){
      notify('error', `Failed to find ${env.toLowerCase()} routing debug for this order.`)
    } else {
      console.log(foundBlob)
      const blobResponse = await apiFetch({
        endpoint: 'blob',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: env === 'PRODUCTION' ? API_PROD_URL : API_DEV_URL,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: foundBlob.name}
        ]
      })

      if(blobResponse.ok){
        setRoutingDebugBlob(JSON.stringify(blobResponse.body))
        setRoutingDebugBlobName(`${env} - ${foundBlob.name}`)
      } else {
        notify('error', `Failed to load order data.`)
      }
    }
    toggleLoading(false)
  }, [])

  const handleRoutingBlobModalClose = useCallback((e) => {
    setRoutingDebugBlob(undefined)
    setRoutingDebugBlobName(undefined)
  }, [])

  //#region PAGINATION
  const totalPages = Math.ceil(filteredRows?.length > 0 ? filteredRows.length / pageSize : orderItems.length / pageSize);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows?.length > 0 ? filteredRows.slice(start, start + pageSize) : orderItems.slice(start, start + pageSize);
  }, [orderItems, currentPage, pageSize, filteredRows]);

  const paginationControls = (
    <div className="mt-3 d-flex justify-content-center align-items-center gap-2 flex-wrap">
      <a href='#top' style={{float: 'left'}}>TOP</a>
      <Button
        variant="secondary"
        disabled={currentPage === 1}
        onClick={() => {setCurrentPage(currentPage - 1);}}
      >
        Previous
      </Button>
  
      {renderPageNumbers()}
  
      <Button
        variant="secondary"
        disabled={currentPage === totalPages}
        onClick={() => {setCurrentPage(currentPage + 1);}}
      >
        Next
      </Button>
    </div>
  );
  
  // Helper to render page buttons with ellipses
  function renderPageNumbers() {
    const pages: (number | 'ellipsis')[] = [];
    const visibleRange = 2;
  
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
  
      if (currentPage > visibleRange + 2) {
        pages.push('ellipsis');
      }
  
      const start = Math.max(2, currentPage - visibleRange);
      const end = Math.min(totalPages - 1, currentPage + visibleRange);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
  
      if (currentPage < totalPages - visibleRange - 1) {
        pages.push('ellipsis');
      }
  
      pages.push(totalPages);
    }
  
    return pages.map((item, idx) =>
      item === 'ellipsis' ? (
        <span key={`ellipsis-${idx}`} className="mx-1 beacon-label">...</span>
      ) : (
        <Button
          key={item}
          variant={item === currentPage ? 'success' : 'outline-secondary'}
          onClick={() => setCurrentPage(item)}
        >
          {item}
        </Button>
      )
    );
  }

  function formatDate(date: string): string {
    return DateTime.fromISO(date).setZone('America/Chicago').toFormat('MM/dd/yyyy t')
  }

  //#region VIEW
  const view = useMemo(() => {
    const {handleShowContent} = props

    return <>
    <div id='top' style={{paddingBottom: '80px'}}>
    <OrderRoutingDebugModalComponent 
        blobData={routingDebugBlob} 
        blobName={routingDebugBlobName} 
        closeModal={handleRoutingBlobModalClose} 
        modalVisible={typeof routingDebugBlob !== 'undefined'} 
        />
    <OrderEdit 
        blobData={copyBlob} blobName={copyBlobOrderNumber} closeModal={() => {setCopyBlob(undefined)}}
        modalVisible={typeof copyBlob !== 'undefined'}
        />
    <CustomError 
      closeError={() => {setError({visible: false})}}
      content={error?.content}
      description={error?.description}
      title={error?.title}
      visible={error?.visible}
    />
    <div>
      <span style={{cursor: 'pointer'}} title='Refresh the table.' onClick={() => {setRefresh(DateTime.now().setZone('America/Chicago').toLocaleString(DateTime.TIME_WITH_SECONDS))}}>
        <RefreshSvg height={20} /></span><span className='beacon-label'> as of {refresh}
      </span>
      <span className='beacon-label'> with {orderItems.length} row(s) loaded and {filteredRows.length} row(s) filtered. The oldest order item here is from {oldestOrderTime}.</span>
    </div>
    <div>
      <table className='shipwise-table'>
        <tbody>
          <tr>
            <td>
              <span className='beacon-label'>PAGE SIZE</span>
              <Form.Control
                type='number'
                step='20'
                style={{width: '150px', height: '30px'}}
                onChange={(e) => setPageSize(+e.currentTarget.value)}
                value={pageSize}
              />
            </td>
            <td>
              <span className='beacon-label'>WEEKS LOOKBACK</span>
                <Form.Control
                  type='number'
                  style={{width: '150px', height: '30px'}}
                  onChange={(e) => setWeeks(+e.currentTarget.value)}
                  value={weeks}
                />
            </td>
            <td>
              <span className='beacon-label'>LIMIT</span>
                <Form.Control
                  type='number'
                  step='50'
                  style={{width: '150px', height: '30px'}}
                  onChange={(e) => setLimit(+e.currentTarget.value)}
                  value={limit}
                />
            </td>
            <td>
              <span className='beacon-label'>ORDER NUMBER</span>
              <Form.Control
                type='text'
                style={{width: '150px', height: '30px'}}
                placeholder='Enter an order number...'
                onChange={handleSearchOrderNumberChanged}
              />
            </td>
            <td>
              <span className='beacon-label'>MPN</span>
              <Form.Control
                type='text'
                style={{width: '150px', height: '30px'}}
                placeholder='Enter an MPN...'
                onChange={handleSearchMpnChanged}
                value={searchMpn}
              />
            </td>
            <td>
              <span className='beacon-label'>SHIPPING STATUS</span>
              <Form.Select
                style={{width: '150px', height: '30px', }}
                onChange={handleShippingStatusChanged}
                value={selectedShippingStatus}
              >
                <option>SELECT A SHIPPING STATUS</option>
                {uniqueShippingStatuses.map(us => (
                  <option key={us} value={us}>{us.toUpperCase()}</option>
                ))}
              </Form.Select>
            </td>
            <td>
              <span className='beacon-label'>ORDER STATUS</span>
              <Form.Select
                style={{width: '150px', height: '30px', }}
                onChange={handleOrderStatusChanged}
                value={selectedOrderStatus}
              >
                <option>SELECT AN ORDER STATUS</option>
                {uniqueOrderStatuses.map(us => (
                  <option key={us} value={us}>{us.toUpperCase()}</option>
                ))}
              </Form.Select>
            </td>
            <td>
              <span className='beacon-label'>FULFILLMENT SOFTWARE</span>
              <Form.Select
                style={{width: '150px', height: '30px', }}
                onChange={handleFulfillmentSoftwareChanged}
                value={selectedFulfillmentSoftware}
              >
                <option>SELECT A FULFILLMENT SOFTWARE</option>
                {uniqueFulfillmentSoftwares.map(us => (
                  <option key={us} value={us}>{us.toUpperCase()}</option>
                ))}
              </Form.Select>
            </td>
            <td>
              <span className='beacon-label'>ACCOUNT</span>
              <Form.Select
                style={{width: '150px', height: '30px', }}
                onChange={handleAccountChanged}
                value={selectedAccount}
              >
                <option>SELECT AN ACCOUNT</option>
                {uniqueAccounts.map(us => (
                  <option key={us} value={us}>{us.toUpperCase()}</option>
                ))}
              </Form.Select>
            </td>
            <td>
              <br/>
              <span onClick={handleClearFiltersClicked} className='beacon-label' style={{cursor: 'pointer'}} title='Clear filters.'>
                <XSvg height={25} /> Clear Filters
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      {limit > 5000 && <><br/><CustomError closeError={undefined} content={undefined} description={'Limit values above 5,000 will take much longer to load.'} title='LIMIT WARNING' visible={true} /></>

      }
    </div>
    <br/>
    <table className='flexport-table'>
      <thead>
        <tr className="sticky-row-2">
          <th></th>
          <th>ORDER</th>
          <th title='Shipping Status'>STATUSES</th>
          <th>ORDER ITEM</th>
          <th>PRODUCT</th>
          <th>DATES</th>
          <th>FULFILLMENT</th>
          <th>SHIP TO</th>
          <th>ACTIONS</th>
        </tr>
      </thead>
      <tbody>
        {paginatedRows.map((oi, index) => (
          <tr key={index}>
            <td><span style={{cursor: 'pointer'}} className='beacon-label' onClick={handleShowContent} data-content={JSON.stringify(oi)} data-title={`${oi.Order?.Name} - Raw Content`}>{oi.index} <EyeSvg height={15} /></span></td>
            <td style={{width: '10%'}}>
              <span className='beacon-label'>
                <a className='beacon-link' title={'View the Account in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${oi.Order?.Account?.Id}`}>{oi.Order?.Account?.Name}</a>
                <br/>
                <a className='beacon-link' title={'View the Order in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${oi.Order?.Id}`}>{oi.Order.Name}</a>
                <br/>
                <br/>
                EST SHIP: <br/>{oi.Order?.Estimated_Ship_Date__c && <Badge bg='secondary' style={{fontSize: 'smaller'}}>{formatDate(oi.Order?.Estimated_Ship_Date__c)}</Badge>}
                <br/>
                EST DELIVER: <br/>{oi.Order?.Deliver_By_Date__c && <Badge bg='secondary' style={{fontSize: 'smaller'}}>{formatDate(oi.Order?.Deliver_By_Date__c)}</Badge>}
              </span>
            </td>
            <td>
              <span className='beacon-label' title='Order Status'>ORDER:<br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Order?.Status__c}</Badge></span>
              <br/>
              <span className='beacon-label' title='Shipping Status'>SHIP DETAIL:<br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Shipping_Status__c ?? 'N/A'}</Badge></span>
              <br/>
              <span className='beacon-label' title='Azure Status'>AZURE:<br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Azure_Status__c ?? 'N/A'}</Badge></span>
            </td>
            <td>
              <span className='beacon-label'>ORDER ITEM: </span>
              <a className='beacon-link' title={'View the Order Item in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${oi?.Id}`}>{oi.Product2?.MPN__c}</a>
              {oi.Master_Order_Product__r?.Product_MPN__c ? <a className='beacon-link' title={'View the Master Order Item in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${oi.Master_Order_Product__r?.Id}`}> ({oi.Master_Order_Product__r?.Product_MPN__c})</a> : ''}
              <br/>
              <span className='beacon-label'>INVENTORY: </span>
              <a className='beacon-link' title={'View the Inventory in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${oi?.Inventory__r?.Id}`}>{oi.Inventory__r?.Name.split('_').map(n => (<>{n}<br/></>))}</a>
              <span className='beacon-label'><Badge bg='secondary' style={{fontSize: 'smaller'}}>x{oi.Quantity}</Badge> @ <Badge bg='secondary' style={{fontSize: 'smaller'}}>${oi.UnitPrice} EA</Badge></span>
              <br/>
              <span className='beacon-label'>FULFILLABLE: <Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Inventory__r?.Fulfillable_Inventory__c}</Badge></span>
            </td>
            <td>
              <span className='beacon-label'>
                <a className='beacon-link' title={'View the Product in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${oi?.Product2?.Id}`}>
                  {oi.Product2?.MPN__c}
                </a>  
                <br/>
                {oi.Product2?.Title__c.split(' - ').map(t => (<>{t}<br/></>))}
                <a className='beacon-link' title={oi.Product2?.Title__c} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${oi?.Product2?.Id}`}>
                  <img src={oi.Product2?.Image_URL_1__c} height={100} width={100} />
                </a>    
              </span>
              
            </td>
            <td>
              <span className='beacon-label' title='The marketplace order time.'>
                ORDER DATE:<br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{formatDate(oi.Order?.Order_Time__c)}</Badge>
              </span>
              <br/>
              <span className='beacon-label' title='When the order was entered into Mission Control.'>
                ENTERED IN MC: <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{formatDate(oi.CreatedDate)}</Badge>
              </span>
              <br/>
              <span className='beacon-label' title='When the order was entered into the Fulfillment Software'>
                SOFTWARE CREATED: <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Shipment_Sent_Time__c && formatDate(oi.Shipping_Detail__r?.Shipment_Sent_Time__c)}</Badge>
              </span>
              <br/>
              <span className='beacon-label' title='When the label was generated.'>
                LABEL CREATED: <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Shipment_Time__c && formatDate(oi.Shipping_Detail__r?.Shipment_Time__c)}</Badge>
                </span>
              <br/>
              <span className='beacon-label' title='When the tracking was sent to the marketplace.'>
                TRACKING SENT: <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Shipment_Uploaded_Time__c && formatDate(oi.Shipping_Detail__r?.Shipment_Uploaded_Time__c)}</Badge>
                </span>
              <br/>
              <span className='beacon-label' title='When the invoice was sent to the marketplace.'>
                INVOICE SENT: <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Invoice_Uploaded_Time__c && formatDate(oi.Shipping_Detail__r?.Invoice_Uploaded_Time__c)}</Badge>
                </span>
            </td>
            <td>
              <span className='beacon-label'>
                FULFILLMENT SOFTWARE: <br/><a className='beacon-link' title={'View the Order in the Fulfillment Software'} target="_blank" rel="noopener noreferrer" href={`${oi.Shipping_Detail__r?.Fulfilled_by_Software_URL__c}`}>{oi.Shipping_Detail__r?.Fulfilled_by_Software__c}</a>
              </span>
              <br/>
              <span className='beacon-label'>
                FULFILLMENT SOFTWARE ID: <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.ShipStation_Order_Id__c}</Badge>
                </span>
              <br/>
              <span className='beacon-label'>
                CARRIER:  <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Shipping_Carrier__c}</Badge>
                </span>
              <br/>
              <span className='beacon-label'>
                TRACKING NUMBER: <br/><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Shipping_Detail__r?.Tracking_Number__c}</Badge>
                {oi.Shipping_Detail__r?.Tracking_Number__c &&
                  <>
                    &nbsp;<a className='beacon-link' title={'View the tracking information on the FedEx site.'} target="_blank" rel="noopener noreferrer" href={`https://www.fedex.com/wtrk/track/?tracknumbers=${oi.Shipping_Detail__r?.Tracking_Number__c}`}>
                      <Button variant='outline-secondary'>FEDEX</Button>
                    </a>
                    &nbsp;<a className='beacon-link' title={'View the tracking information on the UPS site.'} target="_blank" rel="noopener noreferrer" href={`www.ups.com/track?track=yes&trackNums=${oi.Shipping_Detail__r?.Tracking_Number__c}`}>
                      <Button variant='outline-secondary'>UPS</Button>
                    </a>
                    &nbsp;<a className='beacon-link' title={'View the tracking information on the USPS site.'} target="_blank" rel="noopener noreferrer" href={`https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${oi.Shipping_Detail__r?.Tracking_Number__c}`}>
                      <Button variant='outline-secondary'>USPS</Button>
                    </a>
                  </>
                }
                
              </span>
            </td>
            <td>
              <span className='beacon-label'>
                <Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Order?.ShippingCity}, {oi.Order?.ShippingState}</Badge>
                </span>
              <br/>
              <span className='beacon-label'><Badge bg='secondary' style={{fontSize: 'smaller'}}>{oi.Order?.ShippingPostalCode} {oi.Order?.ShippingCountry}</Badge></span>
              <br/>
              <br/>
              <a className='beacon-link' title={'View the postal code in Google Maps.'} target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps?output=search&q=${oi.Order?.ShippingCity}%20${oi.Order?.ShippingState}%20${oi.Order?.ShippingPostalCode}`}>
                <Button variant='outline-secondary'>GOOGLE MAPS</Button>
              </a>
            </td>
            <td>
              <Button 
                variant='secondary'
                data-ordernumber={oi.Order?.Name}
                data-accountid={oi.Order?.Account?.Id}
                data-externalorderid={oi.Order?.External_Order_ID__c}
                onClick={handleSandboxClicked}
                title='Copies order data into Sandbox mapping.'
                >SEND TO SANDBOX</Button>
                <br/><br/>
              <Button
                variant='secondary'
                data-ordernumber={oi.Order?.Name}
                data-externalorderid={oi.Order?.External_Order_ID__c}
                data-env={'PRODUCTION'}
                onClick={handleRoutingDebugClicked}
                title='View production warehouse routing debug information.'
              >
                ROUTING DEBUG (PROD)
              </Button>
              <br/><br/>
              <Button
                variant='secondary'
                data-ordernumber={oi.Order?.Name}
                data-externalorderid={oi.Order?.External_Order_ID__c}
                data-env={'SANDBOX'}
                onClick={handleRoutingDebugClicked}
                title='View sandbox warehouse routing debug information.'
              >
                ROUTING DEBUG (SANDBOX)
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  <div className='sticky-bottom'>
    {paginationControls}
  </div>
  </>
  }, [
    copyBlob, 
    copyBlobOrderNumber, 
    paginatedRows, 
    searchMpn, 
    selectedShippingStatus, 
    selectedFulfillmentSoftware, 
    selectedAccount, 
    searchOrderNumber, 
    limit, 
    weeks, 
    pageSize, 
    oldestOrderTime,
    routingDebugBlob,
    routingDebugBlobName
  ])

  return view
}